// src/services/pdf/sections/HeaderSection.ts
// Renders the PDF header: title, patient identity, programme dates.

import { PDFDocument } from "../core/PDFDocument";
import { PDF_COLORS } from "../core/colors";
import { PAGE_LAYOUT } from "../core/layout";
import type { PDFRenderData } from "../types";
import type { SectionRenderer } from "./SectionRenderer";

export class HeaderSection implements SectionRenderer {
  readonly id = "header";

  render(doc: PDFDocument, data: PDFRenderData): void {
    const { summaryData, generatedAt } = data;
    const { userInfo, meta } = summaryData;

    // Title
    doc.setFont("bold", PAGE_LAYOUT.fontSize.title);
    doc.setColor(PDF_COLORS.brandPrimary);
    doc.text("GP Summary - 6-Week Postnatal Brief", doc.contentLeft);
    doc.advanceY(7);

    // Subtitle
    doc.setFont("normal", PAGE_LAYOUT.fontSize.small);
    doc.setColor(PDF_COLORS.textMuted);
    doc.text("Pre-consultation aid - BodyOwn Recovery Programme", doc.contentLeft);
    doc.advanceY(6);

    doc.drawHorizontalRule();
    doc.advanceY(2);

    // Patient info table (two-column layout)
    const leftCol = doc.contentLeft;
    const rightCol = doc.contentLeft + doc.contentWidth / 2;
    const labelSize = PAGE_LAYOUT.fontSize.small;
    const valueSize = PAGE_LAYOUT.fontSize.body;

    const rows = [
      { label: "Patient", value: userInfo.fullName, label2: "Baby DOB", value2: userInfo.deliveryDate },
      { label: "Date of Birth", value: userInfo.dateOfBirth ?? "-", label2: "Birth Type", value2: userInfo.birthType },
      { label: "GP 6-Week Check", value: userInfo.gpAppointmentDate ?? "Not scheduled", label2: "Programme", value2: meta.dateRange },
    ];

    for (const row of rows) {
      doc.ensureSpace(6);

      // Left column
      doc.setFont("normal", labelSize);
      doc.setColor(PDF_COLORS.textMuted);
      doc.text(row.label + ":", leftCol);
      doc.setFont("bold", valueSize);
      doc.setColor(PDF_COLORS.textPrimary);
      doc.text(row.value, leftCol + 28);

      // Right column
      doc.setFont("normal", labelSize);
      doc.setColor(PDF_COLORS.textMuted);
      doc.text(row.label2 + ":", rightCol);
      doc.setFont("bold", valueSize);
      doc.setColor(PDF_COLORS.textPrimary);
      doc.text(row.value2, rightCol + 28);

      doc.advanceY(5);
    }

    // Generated date
    doc.advanceY(2);
    doc.setFont("italic", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    const genDateStr = generatedAt.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    doc.text(`Generated: ${genDateStr}`, leftCol);
    doc.advanceY(PAGE_LAYOUT.sectionGap);
  }
}
